import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { format as formatDate } from 'date-fns';
import Decimal from 'decimal.js';
import { makeDateStampObject } from './time/makers';
import { fromDateStampObject } from './time/translator';
import { serviceCostModel } from '.';
export var StagesValueType = /*#__PURE__*/ function(StagesValueType) {
    StagesValueType["Percent"] = "percent";
    StagesValueType["Amount"] = "amount";
    return StagesValueType;
}({});
export var StagesTerms = /*#__PURE__*/ function(StagesTerms) {
    StagesTerms["AutoFirst"] = "auto_first";
    StagesTerms["AllManual"] = "all_manual";
    return StagesTerms;
}({});
export var BillingStageScheduleType = /*#__PURE__*/ function(BillingStageScheduleType) {
    BillingStageScheduleType["OnEffectiveDate"] = "onEffectiveDate";
    BillingStageScheduleType["RelativeToEffectiveDate"] = "relativeToEffectiveDate";
    BillingStageScheduleType["OnDate"] = "onDate";
    BillingStageScheduleType["Manual"] = "manual";
    BillingStageScheduleType["RequiresDateSelection"] = "requiresDateSelection";
    return BillingStageScheduleType;
}({});
export var StageScheduleContext = /*#__PURE__*/ function(StageScheduleContext) {
    StageScheduleContext["ProposalOnAcceptance"] = "proposal-on-acceptance";
    StageScheduleContext["ProposalOnEffectiveDate"] = "proposal-on-effective-date";
    StageScheduleContext["Amendment"] = "amendment";
    StageScheduleContext["Template"] = "template";
    return StageScheduleContext;
}({});
export var StageScheduleValidationError = /*#__PURE__*/ function(StageScheduleValidationError) {
    StageScheduleValidationError["SetDate"] = "setDate";
    StageScheduleValidationError["DateBeforeEffectiveDate"] = "dateBeforeEffectiveDate";
    StageScheduleValidationError["DaysRequired"] = "daysRequired";
    StageScheduleValidationError["DaysAtLeastOne"] = "daysAtLeastOne";
    return StageScheduleValidationError;
}({});
const scheduleFromDTO = (dto)=>{
    if (!dto) return undefined;
    switch(dto.type){
        case "onEffectiveDate":
            return {
                type: "onEffectiveDate"
            };
        case "manual":
            return {
                type: "manual"
            };
        case "requiresDateSelection":
            return {
                type: "requiresDateSelection"
            };
        case "relativeToEffectiveDate":
            var _dto_relativeToEffectiveDate;
            return {
                type: "relativeToEffectiveDate",
                relativeToEffectiveDate: (_dto_relativeToEffectiveDate = dto.relativeToEffectiveDate) !== null && _dto_relativeToEffectiveDate !== void 0 ? _dto_relativeToEffectiveDate : {
                    days: 0
                }
            };
        case "onDate":
            return {
                type: "onDate",
                onDate: {
                    date: dto.onDate ? formatDate(fromDateStampObject(dto.onDate.date), 'yyyy-MM-dd') : ''
                }
            };
    }
};
const validateRelativeDays = (days)=>{
    if (days <= 0) return "daysAtLeastOne";
    return null;
};
const validateOnDate = (params)=>{
    const { date, context, effectiveDate, today } = params;
    if (context === "template") {
        if (!date) return null;
        if (date < today) return "setDate";
        return null;
    }
    if (!date) return "setDate";
    switch(context){
        case "proposal-on-effective-date":
            if (effectiveDate && date < effectiveDate) return "dateBeforeEffectiveDate";
            return null;
        case "amendment":
        case "proposal-on-acceptance":
            if (date < today) return "setDate";
            return null;
    }
};
const scheduleToDTO = (schedule)=>{
    if (!schedule) return undefined;
    switch(schedule.type){
        case "onEffectiveDate":
        case "manual":
        case "requiresDateSelection":
            return {
                type: schedule.type
            };
        case "relativeToEffectiveDate":
            return {
                type: schedule.type,
                relativeToEffectiveDate: schedule.relativeToEffectiveDate
            };
        case "onDate":
            {
                if (!schedule.onDate.date) return {
                    type: schedule.type
                };
                const [yearStr, monthStr, dayStr] = schedule.onDate.date.split('-');
                return {
                    type: schedule.type,
                    onDate: {
                        date: makeDateStampObject(parseInt(yearStr, 10), parseInt(monthStr, 10), parseInt(dayStr, 10))
                    }
                };
            }
    }
};
export class OneTimeInStagesModel {
    static fromDTO(dto) {
        return new OneTimeInStagesModel({
            valueType: dto.valueType,
            terms: dto.terms,
            stages: dto.stages.map((stage)=>({
                    stageNumber: stage.stageNumber,
                    value: new Decimal(stage.value).div(100),
                    stageSchedule: scheduleFromDTO(stage.stageSchedule)
                }))
        });
    }
    static defaultStageSchedule() {
        return {
            type: "manual"
        };
    }
    static isStageScheduleValid(params) {
        const { schedule, context, effectiveDate, today } = params;
        if (!schedule) return null;
        switch(schedule.type){
            case "onEffectiveDate":
            case "manual":
                return null;
            case "requiresDateSelection":
                return context === "template" ? null : "setDate";
            case "relativeToEffectiveDate":
                return validateRelativeDays(schedule.relativeToEffectiveDate.days);
            case "onDate":
                return validateOnDate({
                    date: schedule.onDate.date,
                    context,
                    effectiveDate,
                    today
                });
        }
    }
    static forSaveAsTemplate(params) {
        const { stages, today } = params;
        return stages.map((stage)=>{
            const stageSchedule = stage.stageSchedule;
            if (stageSchedule && stageSchedule.type === "onDate" && (!stageSchedule.onDate.date || stageSchedule.onDate.date < today)) {
                return _object_spread_props(_object_spread({}, stage), {
                    stageSchedule: {
                        type: "requiresDateSelection"
                    }
                });
            }
            return stage;
        });
    }
    static make(data) {
        return new OneTimeInStagesModel(data);
    }
    static makeStage(stageNumber, value) {
        return {
            stageNumber,
            value
        };
    }
    toDTO() {
        return {
            valueType: this.data.valueType,
            terms: this.data.terms,
            stages: this.data.stages.map((stage)=>({
                    stageNumber: stage.stageNumber,
                    value: stage.value.mul(100).toNumber(),
                    stageSchedule: scheduleToDTO(stage.stageSchedule)
                }))
        };
    }
    withStageSchedule(index, stageSchedule) {
        const stages = this.data.stages.map((stage, i)=>i === index ? _object_spread_props(_object_spread({}, stage), {
                stageSchedule
            }) : stage);
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    numStages() {
        return this.data.stages.length;
    }
    hasInvalidStages(params) {
        const stages = this.data.stages;
        if (stages.length === 0) return false;
        const valueType = this.data.valueType;
        const isPercentage = valueType === "percent";
        if (stages.some((s)=>s.value.lte(0) || isPercentage && s.value.gte(100))) return true;
        const cost = params === null || params === void 0 ? void 0 : params.cost;
        if (cost) {
            const totalRate = serviceCostModel.price(cost);
            const isValueTypeDollars = valueType === "amount";
            const totalSum = stages.reduce((acc, stage)=>acc.plus(stage.value), new Decimal(0));
            const totalSumExceedsRate = totalSum.gt(isValueTypeDollars ? totalRate : new Decimal(100));
            const valueLevelInvalid = stages.some((stage, index)=>{
                const isLast = index === stages.length - 1;
                if (valueType === "percent" && stage.value.gte(100)) return true;
                if (isValueTypeDollars && stage.value.gt(totalRate)) return true;
                if (valueType === "percent" && !isLast && totalSumExceedsRate) return true;
                if (isValueTypeDollars && !isLast && totalSumExceedsRate) return true;
                if (isLast && stage.value.eq(0)) return true;
                if (stage.value.eq(0)) return true;
                return false;
            });
            if (valueLevelInvalid) return true;
        }
        const scheduleContext = params === null || params === void 0 ? void 0 : params.scheduleContext;
        if (scheduleContext) {
            return stages.some((stage)=>OneTimeInStagesModel.isStageScheduleValid({
                    schedule: stage.stageSchedule,
                    context: scheduleContext.context,
                    effectiveDate: scheduleContext.effectiveDate,
                    today: scheduleContext.today
                }) !== null);
        }
        return false;
    }
    stagesWithFinalAmount(cost) {
        const discountedPrice = serviceCostModel.applyDiscount(cost);
        const price = serviceCostModel.price(cost);
        const isPercentage = this.data.valueType === "percent";
        const stages = this.data.stages.map((stage)=>_object_spread_props(_object_spread({}, stage), {
                discountedFinalAmount: isPercentage ? discountedPrice.mul(stage.value).div(100).toDP(2) : stage.value.mul(discountedPrice).div(price).toDP(2),
                finalAmount: isPercentage ? price.mul(stage.value).div(100).toDP(2) : stage.value
            }));
        if (isPercentage) {
            stages[stages.length - 1].discountedFinalAmount = stages.slice(0, -1).reduce((acc, curr)=>acc.minus(curr.discountedFinalAmount), discountedPrice);
            stages[stages.length - 1].finalAmount = stages.slice(0, -1).reduce((acc, curr)=>acc.minus(curr.finalAmount), price);
        }
        return stages;
    }
    terms() {
        return this.data.terms;
    }
    valueType() {
        return this.data.valueType;
    }
    isAutoFirst() {
        return this.data.terms === "auto_first";
    }
    stages() {
        return this.data.stages;
    }
    setTerms(terms) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            terms
        }));
    }
    setStages(stages) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    setValueType(valueType) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            valueType
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
